import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, CalendarClock } from "lucide-react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const inputCls = "w-full bg-[#F8F9FB] border border-[#E2E5EA] rounded-sm px-2 py-1.5 text-xs outline-none focus:border-[#E32C17]";
const labelCls = "text-[9px] font-bold uppercase text-[#6B7280] block mb-1";

export const ScheduleDialog = ({ open, onClose, query, defaultTitle, onSaved }) => {
  const [title, setTitle] = useState(defaultTitle || "");
  const [frequency, setFrequency] = useState("weekly");
  const [weekday, setWeekday] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [time, setTime] = useState("07:00");
  const [formats, setFormats] = useState(["xlsx"]);
  const [recipients, setRecipients] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const toggleFmt = (f) => setFormats((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));

  const save = async () => {
    const recs = recipients.split(/[,;\s]+/).filter((r) => r.includes("@"));
    if (recs.length === 0) { toast.error("Enter at least one recipient email"); return; }
    if (formats.length === 0) { toast.error("Pick at least one format"); return; }
    setSaving(true);
    try {
      await api.chatScheduleCreate({
        query, title: title || defaultTitle || query, frequency,
        weekday: Number(weekday), day_of_month: Number(dayOfMonth),
        time, formats, recipients: recs,
      });
      toast.success("Report scheduled — it will be emailed with fresh data");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="schedule-dialog">
      <div className="bg-white rounded-sm border border-[#E2E5EA] w-[400px] max-w-[92vw] shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E5EA]">
          <div className="flex items-center gap-2">
            <CalendarClock size={15} className="text-[#E32C17]" />
            <span className="text-xs font-bold uppercase tracking-wider">Schedule this report</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F1F3F5] rounded-sm" data-testid="schedule-close-btn"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[10px] text-[#6B7280] bg-[#F8F9FB] border border-[#E2E5EA] rounded-sm px-2 py-1.5">
            Query: <span className="text-[#1A1D21] font-medium">{query}</span>
          </div>
          <div>
            <label className={labelCls}>Report title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls}
              placeholder={defaultTitle || "Report title"} data-testid="schedule-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Frequency</label>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className={inputCls} data-testid="schedule-frequency-select">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Time (IST)</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid="schedule-time-input" />
            </div>
          </div>
          {frequency === "weekly" && (
            <div>
              <label className={labelCls}>Day of week</label>
              <select value={weekday} onChange={(e) => setWeekday(e.target.value)} className={inputCls} data-testid="schedule-weekday-select">
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
          )}
          {frequency === "monthly" && (
            <div>
              <label className={labelCls}>Day of month (1–28)</label>
              <input type="number" min={1} max={28} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} className={inputCls} data-testid="schedule-dom-input" />
            </div>
          )}
          <div>
            <label className={labelCls}>Formats</label>
            <div className="flex gap-3">
              {["xlsx", "pdf"].map((f) => (
                <label key={f} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={formats.includes(f)} onChange={() => toggleFmt(f)} data-testid={`schedule-fmt-${f}`} />
                  {f === "xlsx" ? "Excel" : "PDF"}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Recipients (comma-separated emails)</label>
            <input value={recipients} onChange={(e) => setRecipients(e.target.value)} className={inputCls}
              placeholder="name@stancortubulars.com, ..." data-testid="schedule-recipients-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[#E2E5EA]">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border border-[#E2E5EA] rounded-sm hover:bg-[#F1F3F5]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="schedule-save-btn"
            className="text-xs px-3 py-1.5 bg-[#E32C17] text-white rounded-sm font-bold hover:bg-[#C41E10] disabled:opacity-50">
            {saving ? "Saving…" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
};
